import { useCallback, useState } from "react";
import { Search } from "lucide-react";
import FilterDropdown from "../components/FilterDropdown";
import Pagination from "../components/Pagination";
import SectionHeader from "../components/SectionHeader";
import StatCard, { StatGrid } from "../components/StatCard";
import Tag from "../components/Tag";
import TextLink from "../components/TextLink";
import controlStyles from "../components/ListControls.module.css";
import tableStyles from "../components/DataTable.module.css";
import RewardDrawer from "./RewardDrawer";
import { REWARD_STATUS } from "./referralsData";
import useWithdrawalRequests from "../useWithdrawalRequests";
import { useDebounced } from "../useApiQuery";
import { formatNumber, formatUsd } from "../format";
import styles from "./ReferralsPage.module.css";

// Trader profit payouts (GET /api/admin/payouts), reviewed like referral rewards. There's
// no Figma frame for this page yet, so it follows the Rewards layout.

const PAGE_SIZE = 10;

const COLUMNS = ["Account ID", "Email", "Type", "Amount", "Status", "Action"];

export const PAYOUT_KINDS = { qualification: "Qualification", funded: "Funded" };

const STATUS_OPTIONS = Object.entries(REWARD_STATUS).map(([value, { label }]) => ({ value, label }));
const KIND_OPTIONS = Object.entries(PAYOUT_KINDS).map(([value, label]) => ({ value, label }));

const usdc = (value) => `${value.toLocaleString("en-US", { maximumFractionDigits: 2 })} USDC`;

// The payout's figures in the drawer, next to the amount being sent
const payoutDetails = (request) => [
  { label: "Payout type", value: PAYOUT_KINDS[request.kind] },
  { label: "MT5 login", value: request.accountId ?? "—" },
  { label: "PRP amount", value: `${request.amountPrp.toLocaleString("en-US")} PRP` },
  { label: "Before fee", value: `${usdc(request.usdc)} (fee ${usdc(request.fee)})` },
];

export default function PayoutsPage() {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState(null);
  const [kind, setKind] = useState(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  const debouncedQuery = useDebounced(query);
  const {
    requests: visible,
    total,
    stats,
    status: loadStatus,
    reload,
    markPaid,
    reject,
  } = useWithdrawalRequests("/api/admin/payouts", {
    page,
    pageSize: PAGE_SIZE,
    status,
    query: debouncedQuery,
    filters: { kind },
  });

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);

  const closeDrawer = useCallback(() => setSelected(null), []);
  const showResult = (result) => {
    if (result.withdrawal) {
      setSelected(result.withdrawal);
    }
    return result;
  };

  const statCards = [
    { label: "Pending requests", value: formatNumber(stats?.pendingCount) },
    { label: "Pending USDC", value: formatUsd(stats?.pendingUsdc) },
    { label: "Total paid", value: formatUsd(stats?.paidUsdc) },
  ];

  return (
    <section className={styles.page}>
      <h1 className={styles.title}>Payouts</h1>

      <section>
        <SectionHeader title="Overview" />
        <StatGrid>
          {statCards.map((stat) => (
            <StatCard key={stat.label} {...stat} />
          ))}
        </StatGrid>
      </section>

      <section>
        <SectionHeader title="Payout requests" />
        <div className={`${controlStyles.toolbar} ${styles.toolbar} d-flex align-items-center justify-content-between`}>
          <div className="d-flex flex-wrap gap-2">
            <FilterDropdown
              label="Status"
              options={STATUS_OPTIONS}
              value={status}
              onChange={(value) => {
                setStatus(value);
                setPage(1);
              }}
            />
            <FilterDropdown
              label="Type"
              options={KIND_OPTIONS}
              value={kind}
              onChange={(value) => {
                setKind(value);
                setPage(1);
              }}
            />
          </div>
          <label className={`${controlStyles.search} d-flex align-items-center`}>
            <Search size={20} strokeWidth={1.75} aria-hidden="true" />
            <input
              type="search"
              placeholder="Search"
              aria-label="Search payout requests"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
            />
          </label>
        </div>

        <div className={tableStyles.wrap}>
          <table className={`${tableStyles.table} ${tableStyles.fixed}`}>
            <thead>
              <tr>
                {COLUMNS.map((column) => (
                  <th key={column} scope="col" className={column === "Action" ? styles.alignEnd : ""}>
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((request) => {
                const requestStatus = REWARD_STATUS[request.status];

                return (
                  <tr key={request.id}>
                    <td>{request.accountId ?? "—"}</td>
                    <td>{request.email}</td>
                    <td>{PAYOUT_KINDS[request.kind]}</td>
                    <td>{usdc(request.amount)}</td>
                    <td>
                      <Tag tone={requestStatus.tone}>{requestStatus.label}</Tag>
                    </td>
                    <td className={styles.alignEnd}>
                      <TextLink onClick={() => setSelected(request)}>
                        {request.status === "requested" ? "Review" : "View details"}
                      </TextLink>
                    </td>
                  </tr>
                );
              })}
              {loadStatus === "loading" ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={controlStyles.empty}>
                    Loading payout requests…
                  </td>
                </tr>
              ) : loadStatus === "error" ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={controlStyles.empty}>
                    We couldn't load payout requests. <TextLink arrow={false} onClick={reload}>Try again</TextLink>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={controlStyles.empty}>
                    {query || status || kind ? "No payout requests match these filters." : "No payout requests yet."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <Pagination page={currentPage} pageCount={pageCount} onChange={setPage} />
      </section>

      <RewardDrawer
        request={selected}
        title="Payout request"
        noun="payout"
        details={selected ? payoutDetails(selected) : []}
        onClose={closeDrawer}
        onMarkPaid={(id, txHash) => markPaid(id, txHash).then(showResult)}
        onReject={(id, reason) => reject(id, reason).then(showResult)}
      />
    </section>
  );
}
